import { Flex, Input } from "@chakra-ui/react";
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import AddImageIcon from "./icons/AddImageIcon";
import CloseIcon from "./icons/CloseIcon";
import type { Document } from "../types/files.types";

interface SmallFileInputProps {
	name: string;
	display?: string;
	maxNumberOfFiles?: number;
	onFileChange?: (files: File[] | null) => void;
	onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
	mandatory?: boolean;
	defaultValue?: File | Document | null;
}

type FileImageInputType = File | Document;

const FileImageInput = forwardRef<
	HTMLInputElement | undefined,
	SmallFileInputProps
>(
	(
		{
			name,
			maxNumberOfFiles,
			onFileChange,
			onChange,
			onBlur,
			display,
			mandatory,
			defaultValue,
		},
		ref,
	) => {
		const [files, setFiles] = useState<FileImageInputType[]>([]);
		const inputRef = useRef<HTMLInputElement>(null);
		useImperativeHandle(ref, () => {
			return inputRef.current ?? undefined;
		});

		useEffect(() => {
			const run = async () => {
				if (defaultValue) {
					setFiles([defaultValue]);
				}
			};
			run();
		}, [defaultValue]);

		const handleOnInput = useCallback(() => {
			if (inputRef.current?.files) {
				const currentFiles = Array.from(inputRef.current.files).slice(
					0,
					maxNumberOfFiles ?? Number.POSITIVE_INFINITY,
				);
				if (currentFiles.length === 0 && files.length !== 0) {
					return;
				}
				if (onFileChange) {
					onFileChange(currentFiles);
				}
				setFiles(currentFiles);
			} else {
				// onFileChange(null);
			}
		}, [onFileChange, maxNumberOfFiles, files.length]);

		const handleOnChange = useCallback(
			(event: React.ChangeEvent<HTMLInputElement>) => {
				if (onChange) {
					onChange(event);
				}
			},
			[onChange],
		);

		const handleOnReset = useCallback(() => {
			if (onFileChange) {
				onFileChange(null);
			}
			if (inputRef.current) {
				inputRef.current.value = "";
			}
			setFiles([]);
		}, [onFileChange]);

		if (Array.isArray(files) && files.length) {
			return (
				<div className="relative">
					<div className="w-[146px] h-[146px] rounded-xl overflow-hidden">
						<img
							src={
								"url" in files[0] ? files[0].url : URL.createObjectURL(files[0])
							}
							alt=""
							className="w-full h-full object-cover aspect-square"
						/>
					</div>
					<div className="absolute -top-3 -right-3">
						<CloseIcon width="28px" height="28px" onClick={handleOnReset} />
					</div>
				</div>
			);
		}
		return (
			<Flex
				display={display}
				bg="brand.50"
				border={mandatory ? "solid 1px" : "dashed 1px"}
				borderColor="brand.700"
				borderRadius="lg"
				justifyContent={"center"}
				direction={"column"}
				alignItems={"center"}
				w={146}
				h={146}
				onClick={() => {
					if (inputRef.current) {
						inputRef.current.click();
					}
				}}
				onDragOver={(event) => {
					event.stopPropagation();
					event.preventDefault();
				}}
				onDrop={(event) => {
					event.preventDefault();
					if (inputRef.current) {
						inputRef.current.files = event.dataTransfer.files;
						setFiles(
							Array.from(inputRef.current.files).slice(
								0,
								maxNumberOfFiles ?? Number.POSITIVE_INFINITY,
							),
						);
						if (onFileChange) {
							onFileChange(
								Array.from(inputRef.current.files).slice(
									0,
									maxNumberOfFiles ?? Number.POSITIVE_INFINITY,
								),
							);
						}
					}
				}}
			>
				<Input
					ref={inputRef}
					type="file"
					name={name}
					display={"none"}
					onInput={handleOnInput}
					onChange={handleOnChange}
					onBlur={onBlur}
					accept="image/png, image/gif, image/jpeg, image/webp"
				></Input>
				<AddImageIcon color="brand.700" w={"34px"} h={"34px"}></AddImageIcon>
			</Flex>
		);
	},
);

export default FileImageInput;
